/**
 * Admin Error Logs UI (US-04-09)
 * View/filter/paginate error_logs + aggregate top errors last 24h/7d
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';

const WORKER_URL = import.meta.env.VITE_WORKER_URL || 'http://localhost:8787';

interface ErrorLog {
  id: string;
  error_id: string;
  user_id: string | null;
  action: string;
  error_message: string;
  input_summary: string | null;
  stack_trace: string | null;
  created_at: string;
  job_id: string | null;
  file_name: string | null;
  retry_count: number | null;
}

interface ErrorAgg {
  action: string;
  count_24h: string;
  count_7d: string;
  last_seen: string;
}

const AdminErrorLogs: React.FC = () => {
  const { session } = useAuth();
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [aggregate, setAggregate] = useState<ErrorAgg[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const limit = 50;

  const token = session?.access_token;

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (actionFilter) params.set('action', actionFilter);
      if (userFilter) params.set('user_id', userFilter);
      const res = await fetch(`${WORKER_URL}/api/admin/error-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json() as { errors: ErrorLog[]; total: number; aggregate: ErrorAgg[] | null };
      setErrors(data.errors);
      setTotal(data.total);
      setAggregate(data.aggregate || []);
    } catch (err) {
      console.error('[AdminErrorLogs] fetch error:', err);
    }
    setLoading(false);
  }, [token, page, actionFilter, userFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyError = (err: ErrorLog) => {
    const text = `ID: ${err.error_id}\nAction: ${err.action}\nUser: ${err.user_id || '(public)'}\nTime: ${err.created_at}\nMessage: ${err.error_message}\n\nStack:\n${err.stack_trace || '(none)'}\n\nInput: ${err.input_summary || '(none)'}`;
    navigator.clipboard.writeText(text);
    setCopiedId(err.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (diffMin < 1) return 'vừa xong';
    if (diffMin < 60) return `${diffMin} phút trước`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h trước`;
    return d.toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-rose-600" />
          <h2 className="text-lg font-bold text-slate-800">Error Logs</h2>
          <span className="text-xs text-slate-500 font-mono">{total} errors</span>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-semibold"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Aggregate top errors */}
      {aggregate.length > 0 && (
        <div className="mb-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3">
          <div className="text-xs font-bold uppercase text-amber-800 mb-2">🔥 Top errors</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {aggregate.slice(0, 8).map(agg => (
              <div
                key={agg.action}
                onClick={() => { setActionFilter(agg.action); setPage(1); }}
                className="bg-white rounded border border-amber-100 p-2 cursor-pointer hover:border-amber-400"
              >
                <div className="text-xs font-bold text-slate-800 truncate">{agg.action}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">24h: {agg.count_24h}</span>
                  <span className="text-[10px] font-mono bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">7d: {agg.count_7d}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input
          type="text"
          placeholder="Filter action (e.g. translate, parse_docx)"
          value={actionFilter}
          onChange={e => { setActionFilter(e.target.value); setPage(1); }}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm w-64"
        />
        <input
          type="text"
          placeholder="User ID (UUID)"
          value={userFilter}
          onChange={e => { setUserFilter(e.target.value); setPage(1); }}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm w-64 font-mono text-xs"
        />
        {(actionFilter || userFilter) && (
          <button
            onClick={() => { setActionFilter(''); setUserFilter(''); setPage(1); }}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Errors table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left w-8"></th>
              <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-600 uppercase">Time</th>
              <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-600 uppercase">Action</th>
              <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-600 uppercase">User</th>
              <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-600 uppercase">Message</th>
              <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-600 uppercase w-16"></th>
            </tr>
          </thead>
          <tbody>
            {loading && errors.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Loading...</td></tr>
            ) : errors.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No errors — app healthy 🎉</td></tr>
            ) : (
              errors.map(err => (
                <React.Fragment key={err.id}>
                  <tr
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === err.id ? null : err.id)}
                  >
                    <td className="px-3 py-2">
                      {expandedId === err.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap">{fmtDate(err.created_at)}</td>
                    <td className="px-3 py-2">
                      <span className="text-[11px] font-mono bg-slate-100 px-1.5 py-0.5 rounded">{err.action}</span>
                    </td>
                    <td className="px-3 py-2 text-[10px] font-mono text-slate-500">
                      {err.user_id ? err.user_id.slice(0, 8) + '…' : <em className="text-slate-400">public</em>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-800 truncate max-w-md">
                      {err.error_message}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); copyError(err); }}
                        className="p-1 hover:bg-slate-200 rounded"
                        title="Copy details"
                      >
                        {copiedId === err.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
                      </button>
                    </td>
                  </tr>
                  {expandedId === err.id && (
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <td colSpan={6} className="px-6 py-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div>
                            <div className="font-bold text-slate-700 mb-1">Error ID</div>
                            <div className="font-mono text-slate-600">{err.error_id}</div>
                          </div>
                          <div>
                            <div className="font-bold text-slate-700 mb-1">Full timestamp</div>
                            <div className="font-mono text-slate-600">{new Date(err.created_at).toISOString()}</div>
                          </div>
                          {err.user_id && (
                            <div>
                              <div className="font-bold text-slate-700 mb-1">User ID</div>
                              <div className="font-mono text-slate-600">{err.user_id}</div>
                            </div>
                          )}
                          {err.job_id && (
                            <div>
                              <div className="font-bold text-slate-700 mb-1">Job ID</div>
                              <div className="font-mono text-slate-600">{err.job_id}</div>
                            </div>
                          )}
                          {err.file_name && (
                            <div>
                              <div className="font-bold text-slate-700 mb-1">File</div>
                              <div className="text-slate-600">{err.file_name}</div>
                            </div>
                          )}
                          {err.input_summary && (
                            <div className="md:col-span-2">
                              <div className="font-bold text-slate-700 mb-1">Context</div>
                              <div className="font-mono text-slate-600 bg-white p-2 rounded border border-slate-200">{err.input_summary}</div>
                            </div>
                          )}
                          {err.stack_trace && (
                            <div className="md:col-span-2">
                              <div className="font-bold text-slate-700 mb-1">Stack trace</div>
                              <pre className="font-mono text-[10px] text-slate-600 bg-white p-2 rounded border border-slate-200 overflow-x-auto max-h-60">
{err.stack_trace}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm">
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50 text-xs"
            >Previous</button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50 text-xs"
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminErrorLogs;
